import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useApp } from '../../context/AppContext'
import { sileo } from 'sileo'
import AuthAccessValidator from '../Auth/AuthAccessValidator'

/**
 * Sección de IA del ticket: evalúa el problema y genera pasos + una solución
 * recomendada con Claude (Edge Function `ticket-solution`), y la persiste en
 * `Tickets.ticketAISolution` (una por ticket; se sobrescribe al regenerar).
 *
 * @param {object} props
 * @param {string|number} props.ticketId
 * @param {object} props.ticket  - El ticket actual (title, description, etc.).
 * @param {Array}  [props.comentarios] - Comentarios del ticket (orden ascendente).
 */
const TicketAISolution = ({ ticketId, ticket, comentarios = [] }) => {
  const { user } = useApp()
  const [record, setRecord] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Cargar la solución de IA guardada (si existe).
  const loadSaved = useCallback(async () => {
    const { data, error: dbError } = await supabase
      .schema('Tickets')
      .from('ticketAISolution')
      .select('*')
      .eq('ticketId', ticketId)
      .maybeSingle()
    if (!dbError) setRecord(data || null)
  }, [ticketId])

  useEffect(() => {
    loadSaved()
  }, [loadSaved])

  const generate = useCallback(async () => {
    if (!ticket) return
    setLoading(true)
    setError(null)
    try {
      // Resolvemos nombres legibles de los catálogos para dar contexto a la IA.
      const [prioRes, catRes, stRes] = await Promise.all([
        ticket.priorityId
          ? supabase
              .schema('Tickets')
              .from('priority')
              .select('name')
              .eq('id', ticket.priorityId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        ticket.categoryId
          ? supabase
              .schema('Tickets')
              .from('category')
              .select('name')
              .eq('id', ticket.categoryId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        ticket.statusId
          ? supabase
              .schema('Tickets')
              .from('status')
              .select('name')
              .eq('id', ticket.statusId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ])

      const contexto = {
        titulo: ticket.title,
        descripcion: ticket.description,
        tipo: ticket.type,
        origen: ticket.systemOrigin,
        prioridad: prioRes?.data?.name || null,
        categoria: catRes?.data?.name || null,
        estado: stRes?.data?.name || null,
      }

      const { data, error: fnError } = await supabase.functions.invoke(
        'ticket-solution',
        {
          body: {
            ticket: contexto,
            comentarios: comentarios
              .map((c) => c?.content || c?.body || '')
              .filter(Boolean),
          },
        }
      )
      if (fnError) throw fnError
      if (data?.error) throw new Error(data.error)

      const solution = data?.solution
      if (!solution) throw new Error('Respuesta vacía de la IA')

      // Persistir (upsert: una solución de IA por ticket).
      const payload = {
        ticketId: Number(ticketId),
        evaluacion: solution.evaluacion || '',
        pasos: solution.pasos || [],
        solucion: solution.solucion || '',
        modelo: data.modelo || 'claude-opus-4-8',
        createdBy: user?.id || null,
        updatedAt: new Date().toISOString(),
      }
      const { data: saved, error: saveError } = await supabase
        .schema('Tickets')
        .from('ticketAISolution')
        .upsert(payload, { onConflict: 'ticketId' })
        .select()
        .maybeSingle()
      if (saveError) throw saveError

      setRecord(saved)
      sileo.success('Solución generada y guardada')
    } catch (err) {
      console.error('Error solución IA:', err)
      setError('No se pudo generar la solución. Intenta de nuevo.')
    } finally {
      setLoading(false)
    }
  }, [ticket, ticketId, comentarios, user])

  const pasos = Array.isArray(record?.pasos) ? record.pasos : []

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="section-subtitle m-0">
          <i className="fa-solid fa-wand-magic-sparkles me-2 text-primary"></i>
          Evaluación y solución con IA
        </h6>

        <AuthAccessValidator AccessNames={['ticket_support']} showError={false}>
          <button
            className="btn btn-sm btn-outline-primary no-print"
            onClick={generate}
            disabled={loading}
          >
            <i
              className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'} me-1`}
            ></i>
            {loading ? 'Generando…' : record ? 'Regenerar' : 'Generar con IA'}
          </button>
        </AuthAccessValidator>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {!record && !loading && !error && (
        <p className="text-body-secondary small mb-0">
          Aún no hay una evaluación generada. Usa{' '}
          <strong>Generar con IA</strong> para obtener un diagnóstico, pasos de
          resolución y una solución recomendada a partir de los datos del
          ticket.
        </p>
      )}

      {loading && !record && (
        <p className="text-body-secondary small mb-0">
          Analizando el ticket y generando la solución…
        </p>
      )}

      {record && (
        <div className="ai-solution">
          {record.evaluacion && (
            <>
              <div className="ai-label">Evaluación</div>
              <p className="mb-3">{record.evaluacion}</p>
            </>
          )}

          {pasos.length > 0 && (
            <>
              <div className="ai-label">Pasos de resolución</div>
              <ol className="mb-3 ps-3">
                {pasos.map((p, i) => (
                  <li key={i} className="mb-1">
                    {p}
                  </li>
                ))}
              </ol>
            </>
          )}

          {record.solucion && (
            <>
              <div className="ai-label">Solución recomendada</div>
              <p className="mb-2">{record.solucion}</p>
            </>
          )}

          <div
            className="text-body-secondary fst-italic"
            style={{ fontSize: 11 }}
          >
            Generado por IA ({record.modelo || 'claude-opus-4-8'})
            {record.updatedAt
              ? ` · ${new Date(record.updatedAt).toLocaleString('es')}`
              : ''}{' '}
            · Verifica antes de aplicar.
          </div>
        </div>
      )}

      <style>{`
        .ai-solution .ai-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          color: var(--gm-text-muted, #94a3b8);
          margin-bottom: 4px;
        }
      `}</style>
    </div>
  )
}

export default TicketAISolution
